"use client";
// Imports
import { memo, useDeferredValue, useEffect, useMemo } from "react";
import { useSearchParams } from "next/navigation";

import {
  Container,
  Card,
  FilterColumn,
  DayFilterBar,
  Pagination,
  ComponentWrapper,
  EventsFilterDrawer,
  TimePeriodFilter,
} from "@/components";
import {
  refineResults,
  getCategoriesByDay,
  getLocationsByDay,
  getTypesByDay,
  filterEventsByTimePeriod,
  groupEventsByTimePeriod,
} from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { getEventsByDay } from "@/lib/helpersTwo";
import useStore from "@/lib/state";
import { useKindeSession } from "@/components/KindeWrapper";

// Showbags Grid
export function EventsGrid({
  type,
  showFilter,
  showPagination,
  events,
  collectionHandle,
  days,
  showDates,
  mapIsActive,
}) {
  const { user } = useKindeSession();
  const userId = user?.id || null;

  // Variables
  const searchParams = useSearchParams();
  const {
    sortBy,
    searchTerm,
    activeCategories,
    currentPage,
    pageBy,
    setSortBy,
    setSearchTerm,
    setActiveCategories,
    setCurrentPage,
    setPageBy,
    activeLocations,
    setActiveLocations,
    activeTypes,
    setActiveTypes,
    selectedDay,
    selectedTimePeriod,
    setSelectedTimePeriod,
  } = useStore();
  const deferredSelectedDay = useDeferredValue(selectedDay);
  const isDayFilterPending = deferredSelectedDay.slug !== selectedDay.slug;

  // Set State from URL Params
  useEffect(() => {
        // Get current URL params as strings for comparison
    const urlSortBy = searchParams.get("sortBy");
    const urlSearchTerm = searchParams.get("searchTerm");
    const urlActiveCategories = searchParams.get("activeCategories");
    const urlActiveLocations = searchParams.get("activeLocations");
    const urlActiveTypes = searchParams.get("activeTypes");
    const urlCurrentPage = searchParams.get("currentPage");
    const urlPageBy = searchParams.get("pageBy");
    const urlTimePeriod = searchParams.get("timePeriod");

    // Only update state if the URL actually contains filter parameters
    // This prevents resetting filters when only the 'event' parameter changes
    if (urlSortBy || urlSearchTerm || urlActiveCategories || urlActiveLocations || urlActiveTypes || urlCurrentPage || urlPageBy || urlTimePeriod) {
      setSortBy(urlSortBy || "time-grouped");
      setSearchTerm(urlSearchTerm || "");
      setActiveCategories(urlActiveCategories?.split(",") || []);
      setActiveLocations(urlActiveLocations?.split(",") || []);
      setActiveTypes(urlActiveTypes?.split(",") || []);
      setSelectedTimePeriod(urlTimePeriod || "All");
      setCurrentPage(parseInt(urlCurrentPage) || 1);

      if (showPagination) {
        if (parseInt(urlPageBy) > 100) {
          setPageBy(24);
        } else {
          setPageBy(urlPageBy || 24);
        }
      } else {
        setPageBy(events.length);
      }
    } else {
      // If no filter params in URL, only set defaults on first load
      // Check if this is likely the first load by seeing if we have no current state
      if (!sortBy || sortBy === "a-z") {
        setSortBy("time-grouped");
      }
      if (!selectedTimePeriod) {
        setSelectedTimePeriod("All");
      }
      if (showPagination && !pageBy) {
        setPageBy(24);
      } else if (!showPagination) {
        setPageBy(events.length);
      }
    }
  }, [searchParams, showPagination, events.length, sortBy, pageBy, selectedTimePeriod]);

  // Refine results once per filter change. Search-param updates can re-render
  // this component, but should not repeat the full event scheduling pipeline.
  const {
    categoriesForDays,
    locationsForDays,
    typesForDays,
    refinedResults,
    totalResults,
  } = useMemo(() => {
    const datedEvents = getEventsByDay(events, deferredSelectedDay, showDates);
    const timePeriodFilteredEvents = selectedTimePeriod === "All"
      ? datedEvents
      : filterEventsByTimePeriod(
          datedEvents,
          selectedTimePeriod,
          deferredSelectedDay,
          showDates
        );
    const nextRefinedResults = refineResults(
      timePeriodFilteredEvents,
      sortBy,
      searchTerm,
      activeCategories,
      pageBy,
      currentPage,
      collectionHandle,
      activeLocations,
      activeTypes,
      undefined
    );

    return {
      categoriesForDays: getCategoriesByDay(timePeriodFilteredEvents),
      locationsForDays: getLocationsByDay(timePeriodFilteredEvents),
      typesForDays: getTypesByDay(timePeriodFilteredEvents),
      refinedResults: nextRefinedResults,
      totalResults: nextRefinedResults?.flat().length ?? 0,
    };
  }, [
    events,
    deferredSelectedDay,
    showDates,
    selectedTimePeriod,
    sortBy,
    searchTerm,
    activeCategories,
    pageBy,
    currentPage,
    collectionHandle,
    activeLocations,
    activeTypes,
  ]);

  const currentResults = refinedResults[currentPage - 1] ?? [];
  const groupedEvents = useMemo(
    () =>
      selectedTimePeriod === "All" && collectionHandle === "events"
        ? groupEventsByTimePeriod(
            currentResults,
            deferredSelectedDay,
            showDates
          )
        : null,
    [
      collectionHandle,
      currentResults,
      deferredSelectedDay,
      selectedTimePeriod,
      showDates,
    ]
  );

  const startAmount = (currentPage - 1) * pageBy + 1;
  const endAmount = Math.min(
    currentPage * pageBy,
    totalResults
  );

  // useEffect to push back to page 1 if current page exceeds the new total amount of <results></results>
  useEffect(() => {
    const totalPages = Math.ceil(totalResults / pageBy);
    if (currentPage > totalPages && totalResults > 0) {
      setCurrentPage(1);
    }
  }, [totalResults, currentPage, pageBy, setCurrentPage]);

  // What's on / events: red gum accents instead of theme saddle orange
  const eventsThemeStyle =
    collectionHandle === "events"
      ? {
          "--button-background": "137 33 2",
          "--button-hover-background": "137 33 2",
          "--button-foreground": "255 255 255",
        }
      : undefined;

  // Render
  return (
    <ComponentWrapper type={type}>
      <Container>
        <div className="w-full" style={eventsThemeStyle}>
          <DayFilterBar days={days} />
          <div className="flex flex-col justify-center items-center w-full lg:justify-end lg:items-start">
            <div className="flex col-span-full justify-between items-center pb-0 w-full lg:pb-8">
              <EventsFilterDrawer
                categories={categoriesForDays}
                locations={locationsForDays}
                types={typesForDays}
              />
              <p className="hidden lg:block">
                Showing {startAmount}-{endAmount} of {totalResults} results
              </p>
              <TimePeriodFilter />
            </div>
            <p className="flex justify-center pt-4 w-full lg:hidden">
              Showing {startAmount}-{endAmount} of {totalResults} results
            </p>
          </div>
          <div className="grid grid-cols-4 gap-4">
            {showFilter && (
              <FilterColumn
                results={{ startAmount, endAmount, totalResults }}
                categories={categoriesForDays}
                locations={locationsForDays}
                types={typesForDays}
                searchParams={searchParams}
              />
            )}

            <div
              className={`relative col-span-4 transition-opacity duration-150 lg:col-span-3 ${
                isDayFilterPending ? "opacity-60" : "opacity-100"
              }`}
              aria-busy={isDayFilterPending}
            >
              {isDayFilterPending && (
                <div
                  className="sticky top-24 z-20 flex justify-center pointer-events-none"
                  role="status"
                  aria-live="polite"
                >
                  <span className="px-4 py-2 font-bold bg-white rounded-full shadow-lg text-brand-red-gum">
                    Updating events…
                  </span>
                </div>
              )}

              <EventResults
                collectionHandle={collectionHandle}
                currentResults={currentResults}
                groupedEvents={groupedEvents}
                mapIsActive={mapIsActive}
                selectedDay={deferredSelectedDay}
                selectedTimePeriod={selectedTimePeriod}
                showDates={showDates}
                userId={userId}
              />
            </div>

            <div className="col-span-3 w-full">
              {!showPagination ||
              totalResults - currentPage * pageBy <
                parseInt(pageBy) / 2 ? null : (
                <Pagination
                  isResultMoreThenPage={
                    totalResults - currentPage * pageBy <
                    parseInt(pageBy) / 2
                  }
                  searchParams={searchParams}
                />
              )}
            </div>
          </div>
        </div>
      </Container>
    </ComponentWrapper>
  );
}

const EventResults = memo(function EventResults({
  collectionHandle,
  currentResults,
  groupedEvents,
  mapIsActive,
  selectedDay,
  selectedTimePeriod,
  showDates,
  userId,
}) {
  if (currentResults.length === 0) {
    return (
      <div className="flex justify-center items-center h-full text-lg bg-brand-wool rounded-lg border lg:h-96">
        <p className="text-lg">No results found</p>
      </div>
    );
  }

  if (selectedTimePeriod === "All" && collectionHandle === "events") {
    return (
      <div className="space-y-8">
        {["Morning", "Afternoon", "Nightshift", "Unknown"].map((period) => {
          if (groupedEvents[period].length === 0) return null;

          const periodLabel = period === "Nightshift" ? "Evening" : period;
          return (
            <div key={period} className="space-y-4">
              <h3 className="text-xl py-4 text-center font-bold text-gray-800 lg:py-10">
                {periodLabel}
              </h3>
              <div className="grid grid-cols-2 gap-4 w-full md:grid-cols-3 lg:grid-cols-3">
                {groupedEvents[period].map((event, index) => (
                  <Card
                    userId={userId}
                    mapIsActive={mapIsActive}
                    collectionHandle={collectionHandle}
                    key={`${period}-${event.id ?? event.slug}`}
                    index={index}
                    instanceId={`${event.slug}-${period}-${index}`}
                    data={event}
                    selectedDay={selectedDay}
                    showDates={showDates}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 col-span-4 gap-4 w-full md:grid-cols-3 lg:col-span-3 lg:grid-cols-3">
      {currentResults.map((page, index) => (
        <Card
          userId={userId}
          mapIsActive={mapIsActive}
          collectionHandle={collectionHandle}
          key={page.id ?? page.slug ?? index}
          index={index}
          instanceId={`${page.slug}-${selectedTimePeriod}-${index}`}
          data={page}
          selectedDay={selectedDay}
          showDates={showDates}
        />
      ))}
    </div>
  );
});
